/**
 * AuthContext — single source of truth for auth in Plotted.
 *
 * Provides: session, loading, signOut
 * Handles: getSession, onAuthStateChange, proactive visibility refresh
 *
 * Usage:
 *   <AuthProvider><App /></AuthProvider>
 *   const { session, loading, signOut } = useAuth()
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Auth state listener ─────────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── Proactive token refresh on tab visibility ───────────────────────────
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') supabase.auth.getSession();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  // ── Sign out ────────────────────────────────────────────────────────────
  const signOut = useCallback(async () => {
    setSession(null);
    await supabase.auth.signOut({ scope: 'local' });
  }, []);

  return (
    <AuthContext.Provider value={{ session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
